import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { reportsService } from '@/services/reports';
import { RECIPIENTS_PAGE_SIZE } from '../constants';
import type {
  Recipient,
  RecipientSort,
  RecipientSortKey,
  RecipientStatusFilter,
} from '../types';
import {
  filterRecipients,
  mapReportSendUser,
  nextSort,
  sortRecipients,
} from '../utils';

export function useRecipientsList(reportId?: string) {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<RecipientStatusFilter>('todos');
  const [sort, setSort] = useState<RecipientSort | null>(null);
  const [offset, setOffset] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const scrollRootRef = useRef<HTMLDivElement | null>(null);
  const selectionTouched = useRef(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const applyFetchedUsers = useCallback(
    (items: Recipient[], reset: boolean) => {
      setRecipients((current) => (reset ? items : [...current, ...items]));
      if (!selectionTouched.current) {
        setSelectedIds((current) => {
          const next = reset ? new Set<string>() : new Set(current);
          items.forEach((item) => {
            if (!item.alreadySent) next.add(item.id);
          });
          return next;
        });
      }
    },
    []
  );

  const fetchUsers = useCallback(
    async (reset: boolean, currentOffset: number) => {
      if (!reportId) return;
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      if (reset) setLoading(true);
      else setLoadingMore(true);

      try {
        const response = await reportsService.getReportUsers(reportId, {
          offset: currentOffset,
          limit: RECIPIENTS_PAGE_SIZE,
          ...(debouncedSearch ? { q: debouncedSearch } : {}),
        });
        if (requestId !== requestIdRef.current) return;
        const mapped = response.data.map(mapReportSendUser);
        applyFetchedUsers(mapped, reset);
        setOffset(currentOffset + response.data.length);
        setRemaining(response.remaining);
      } catch {
        if (requestId !== requestIdRef.current) return;
        if (reset) {
          setRecipients([]);
          setOffset(0);
          setRemaining(0);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [applyFetchedUsers, debouncedSearch, reportId]
  );

  useEffect(() => {
    if (!reportId) return;
    selectionTouched.current = false;
    setOffset(0);
    setRemaining(0);
    void fetchUsers(true, 0);
  }, [debouncedSearch, fetchUsers, reportId]);

  const filteredRecipients = useMemo(
    () => sortRecipients(filterRecipients(recipients, '', statusFilter), sort),
    [recipients, statusFilter, sort]
  );

  const hasMore = remaining > 0;

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore) return;
    void fetchUsers(false, offset);
  }, [fetchUsers, hasMore, loading, loadingMore, offset]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRootRef.current;
    if (!sentinel || !root || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMore();
        }
      },
      { root, rootMargin: '24px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore, filteredRecipients.length]);

  const sendableSelectedIds = useMemo(
    () =>
      [...selectedIds].filter((id) => {
        const recipient = recipients.find((item) => item.id === id);
        return Boolean(recipient && !recipient.alreadySent);
      }),
    [recipients, selectedIds]
  );

  const selectedCount = sendableSelectedIds.length;

  const selectableFiltered = filteredRecipients.filter((recipient) => !recipient.alreadySent);

  const filteredSelectedCount = selectableFiltered.filter((recipient) =>
    selectedIds.has(recipient.id)
  ).length;

  const headerSelectionState: boolean | 'indeterminate' =
    selectableFiltered.length === 0 || filteredSelectedCount === 0
      ? false
      : filteredSelectedCount === selectableFiltered.length
        ? true
        : 'indeterminate';

  const toggleRecipient = (id: string) => {
    const recipient = recipients.find((item) => item.id === id);
    if (recipient?.alreadySent) return;
    selectionTouched.current = true;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    const ids = selectableFiltered.map((recipient) => recipient.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
    selectionTouched.current = true;

    setSelectedIds((current) => {
      const next = new Set(current);
      if (allSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleSort = (key: RecipientSortKey) => {
    setSort((current) => nextSort(current, key));
  };

  return {
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    sort,
    toggleSort,
    visibleRecipients: filteredRecipients,
    hasMore,
    loadMore,
    loading,
    sentinelRef,
    scrollRootRef,
    selectedIds,
    selectedCount,
    sendableSelectedIds,
    headerSelectionState,
    toggleRecipient,
    toggleSelectAllFiltered,
  };
}
